import type { ReactElement } from 'react';

interface ProfilePictureProps {
  animal?: string | null;
  size?: number;
  className?: string;
}

const animals: Record<string, { bg: string; content: ReactElement }> = {
  cat: {
    bg: '#f472b6',
    content: (
      <>
        {/* Ears */}
        <polygon points="25,35 35,15 45,35" fill="#f472b6" />
        <polygon points="55,35 65,15 75,35" fill="#f472b6" />
        <polygon points="28,33 35,18 42,33" fill="#fbcfe8" />
        <polygon points="58,33 65,18 72,33" fill="#fbcfe8" />
        {/* Face */}
        <circle cx="50" cy="55" r="30" fill="#f472b6" />
        {/* Eyes */}
        <ellipse cx="40" cy="50" rx="5" ry="6" fill="#1f2937" />
        <ellipse cx="60" cy="50" rx="5" ry="6" fill="#1f2937" />
        <circle cx="42" cy="48" r="2" fill="white" />
        <circle cx="62" cy="48" r="2" fill="white" />
        {/* Nose */}
        <ellipse cx="50" cy="62" rx="4" ry="3" fill="#1f2937" />
        {/* Mouth */}
        <path d="M46 67 Q50 72 54 67" stroke="#1f2937" strokeWidth="2" fill="none" />
        {/* Whiskers */}
        <line x1="20" y1="58" x2="35" y2="60" stroke="#1f2937" strokeWidth="1.5" />
        <line x1="20" y1="65" x2="35" y2="65" stroke="#1f2937" strokeWidth="1.5" />
        <line x1="65" y1="60" x2="80" y2="58" stroke="#1f2937" strokeWidth="1.5" />
        <line x1="65" y1="65" x2="80" y2="65" stroke="#1f2937" strokeWidth="1.5" />
      </>
    ),
  },
  dog: {
    bg: '#a78bfa',
    content: (
      <>
        {/* Ears */}
        <ellipse cx="28" cy="35" rx="12" ry="20" fill="#7c3aed" />
        <ellipse cx="72" cy="35" rx="12" ry="20" fill="#7c3aed" />
        {/* Face */}
        <circle cx="50" cy="55" r="30" fill="#a78bfa" />
        {/* Snout */}
        <ellipse cx="50" cy="65" rx="15" ry="12" fill="#ddd6fe" />
        {/* Eyes */}
        <circle cx="38" cy="48" r="6" fill="#1f2937" />
        <circle cx="62" cy="48" r="6" fill="#1f2937" />
        <circle cx="40" cy="46" r="2" fill="white" />
        <circle cx="64" cy="46" r="2" fill="white" />
        {/* Nose */}
        <ellipse cx="50" cy="60" rx="6" ry="5" fill="#1f2937" />
        {/* Tongue */}
        <ellipse cx="50" cy="75" rx="5" ry="6" fill="#f472b6" />
      </>
    ),
  },
  bear: {
    bg: '#92400e',
    content: (
      <>
        {/* Ears */}
        <circle cx="28" cy="30" r="12" fill="#78350f" />
        <circle cx="72" cy="30" r="12" fill="#78350f" />
        <circle cx="28" cy="30" r="6" fill="#d6b38a" />
        <circle cx="72" cy="30" r="6" fill="#d6b38a" />
        {/* Face */}
        <circle cx="50" cy="55" r="32" fill="#92400e" />
        {/* Snout */}
        <ellipse cx="50" cy="65" rx="14" ry="11" fill="#d6b38a" />
        {/* Eyes */}
        <circle cx="38" cy="48" r="5" fill="#1f2937" />
        <circle cx="62" cy="48" r="5" fill="#1f2937" />
        <circle cx="40" cy="46" r="2" fill="white" />
        <circle cx="64" cy="46" r="2" fill="white" />
        {/* Nose */}
        <ellipse cx="50" cy="62" rx="5" ry="4" fill="#1f2937" />
      </>
    ),
  },
  fox: {
    bg: '#f97316',
    content: (
      <>
        {/* Ears */}
        <polygon points="22,40 30,10 42,40" fill="#f97316" />
        <polygon points="58,40 70,10 78,40" fill="#f97316" />
        <polygon points="26,38 30,18 38,38" fill="white" />
        <polygon points="62,38 70,18 74,38" fill="white" />
        {/* Face */}
        <ellipse cx="50" cy="58" rx="28" ry="26" fill="#f97316" />
        {/* White face markings */}
        <ellipse cx="50" cy="68" rx="18" ry="16" fill="white" />
        {/* Eyes */}
        <ellipse cx="38" cy="52" rx="4" ry="5" fill="#1f2937" />
        <ellipse cx="62" cy="52" rx="4" ry="5" fill="#1f2937" />
        <circle cx="40" cy="50" r="1.5" fill="white" />
        <circle cx="64" cy="50" r="1.5" fill="white" />
        {/* Nose */}
        <ellipse cx="50" cy="65" rx="4" ry="3" fill="#1f2937" />
      </>
    ),
  },
  owl: {
    bg: '#8b5cf6',
    content: (
      <>
        {/* Ear tufts */}
        <polygon points="28,30 35,10 42,30" fill="#6d28d9" />
        <polygon points="58,30 65,10 72,30" fill="#6d28d9" />
        {/* Face */}
        <ellipse cx="50" cy="55" rx="30" ry="28" fill="#8b5cf6" />
        {/* Eye circles */}
        <circle cx="38" cy="50" r="14" fill="#ddd6fe" />
        <circle cx="62" cy="50" r="14" fill="#ddd6fe" />
        {/* Eyes */}
        <circle cx="38" cy="50" r="8" fill="#1f2937" />
        <circle cx="62" cy="50" r="8" fill="#1f2937" />
        <circle cx="41" cy="47" r="3" fill="white" />
        <circle cx="65" cy="47" r="3" fill="white" />
        {/* Beak */}
        <polygon points="50,58 45,68 50,75 55,68" fill="#fbbf24" />
      </>
    ),
  },
  rabbit: {
    bg: '#e5e7eb',
    content: (
      <>
        {/* Ears */}
        <ellipse cx="35" cy="25" rx="8" ry="22" fill="#e5e7eb" />
        <ellipse cx="65" cy="25" rx="8" ry="22" fill="#e5e7eb" />
        <ellipse cx="35" cy="25" rx="4" ry="18" fill="#f9a8d4" />
        <ellipse cx="65" cy="25" rx="4" ry="18" fill="#f9a8d4" />
        {/* Face */}
        <circle cx="50" cy="60" r="28" fill="#e5e7eb" />
        {/* Eyes */}
        <circle cx="40" cy="55" r="5" fill="#1f2937" />
        <circle cx="60" cy="55" r="5" fill="#1f2937" />
        <circle cx="42" cy="53" r="2" fill="white" />
        <circle cx="62" cy="53" r="2" fill="white" />
        {/* Nose */}
        <ellipse cx="50" cy="67" rx="4" ry="3" fill="#f9a8d4" />
        {/* Cheeks */}
        <circle cx="35" cy="68" r="6" fill="#fce7f3" />
        <circle cx="65" cy="68" r="6" fill="#fce7f3" />
      </>
    ),
  },
  penguin: {
    bg: '#1f2937',
    content: (
      <>
        {/* Body */}
        <ellipse cx="50" cy="58" rx="28" ry="30" fill="#1f2937" />
        {/* White belly */}
        <ellipse cx="50" cy="62" rx="18" ry="22" fill="white" />
        {/* Eyes */}
        <circle cx="40" cy="48" r="6" fill="white" />
        <circle cx="60" cy="48" r="6" fill="white" />
        <circle cx="40" cy="48" r="4" fill="#1f2937" />
        <circle cx="60" cy="48" r="4" fill="#1f2937" />
        <circle cx="41" cy="47" r="1.5" fill="white" />
        <circle cx="61" cy="47" r="1.5" fill="white" />
        {/* Beak */}
        <polygon points="50,54 44,62 50,66 56,62" fill="#f97316" />
      </>
    ),
  },
  lion: {
    bg: '#fbbf24',
    content: (
      <>
        {/* Mane */}
        <circle cx="50" cy="50" r="38" fill="#d97706" />
        {/* Face */}
        <circle cx="50" cy="55" r="25" fill="#fbbf24" />
        {/* Ears */}
        <circle cx="30" cy="38" r="8" fill="#fbbf24" />
        <circle cx="70" cy="38" r="8" fill="#fbbf24" />
        {/* Eyes */}
        <circle cx="42" cy="52" r="5" fill="#1f2937" />
        <circle cx="58" cy="52" r="5" fill="#1f2937" />
        <circle cx="43" cy="50" r="2" fill="white" />
        <circle cx="59" cy="50" r="2" fill="white" />
        {/* Snout */}
        <ellipse cx="50" cy="65" rx="10" ry="8" fill="#fef3c7" />
        {/* Nose */}
        <ellipse cx="50" cy="63" rx="4" ry="3" fill="#1f2937" />
      </>
    ),
  },
  elephant: {
    bg: '#9ca3af',
    content: (
      <>
        {/* Ears */}
        <ellipse cx="22" cy="50" rx="16" ry="22" fill="#6b7280" />
        <ellipse cx="78" cy="50" rx="16" ry="22" fill="#6b7280" />
        {/* Face */}
        <circle cx="50" cy="50" r="28" fill="#9ca3af" />
        {/* Eyes */}
        <circle cx="40" cy="45" r="5" fill="#1f2937" />
        <circle cx="60" cy="45" r="5" fill="#1f2937" />
        <circle cx="42" cy="43" r="2" fill="white" />
        <circle cx="62" cy="43" r="2" fill="white" />
        {/* Trunk */}
        <path
          d="M50 55 Q50 70 45 80 Q43 85 50 85 Q57 85 55 80 Q50 70 50 55"
          fill="#9ca3af"
          stroke="#6b7280"
          strokeWidth="2"
        />
      </>
    ),
  },
  monkey: {
    bg: '#a3623a',
    content: (
      <>
        {/* Ears */}
        <circle cx="22" cy="50" r="12" fill="#a3623a" />
        <circle cx="78" cy="50" r="12" fill="#a3623a" />
        <circle cx="22" cy="50" r="7" fill="#f5d5c8" />
        <circle cx="78" cy="50" r="7" fill="#f5d5c8" />
        {/* Face */}
        <circle cx="50" cy="52" r="28" fill="#a3623a" />
        {/* Face patch */}
        <ellipse cx="50" cy="60" rx="20" ry="18" fill="#f5d5c8" />
        {/* Eyes */}
        <circle cx="42" cy="48" r="5" fill="#1f2937" />
        <circle cx="58" cy="48" r="5" fill="#1f2937" />
        <circle cx="43" cy="46" r="2" fill="white" />
        <circle cx="59" cy="46" r="2" fill="white" />
        {/* Nostrils */}
        <circle cx="46" cy="62" r="3" fill="#1f2937" />
        <circle cx="54" cy="62" r="3" fill="#1f2937" />
        {/* Smile */}
        <path d="M42 72 Q50 78 58 72" stroke="#1f2937" strokeWidth="2" fill="none" />
      </>
    ),
  },
  panda: {
    bg: '#f3f4f6',
    content: (
      <>
        {/* Ears */}
        <circle cx="28" cy="30" r="12" fill="#1f2937" />
        <circle cx="72" cy="30" r="12" fill="#1f2937" />
        {/* Face */}
        <circle cx="50" cy="55" r="30" fill="#f3f4f6" />
        {/* Eye patches */}
        <ellipse cx="38" cy="50" rx="12" ry="10" fill="#1f2937" />
        <ellipse cx="62" cy="50" rx="12" ry="10" fill="#1f2937" />
        {/* Eyes */}
        <circle cx="38" cy="50" r="5" fill="white" />
        <circle cx="62" cy="50" r="5" fill="white" />
        <circle cx="39" cy="49" r="3" fill="#1f2937" />
        <circle cx="63" cy="49" r="3" fill="#1f2937" />
        {/* Nose */}
        <ellipse cx="50" cy="65" rx="5" ry="4" fill="#1f2937" />
      </>
    ),
  },
  koala: {
    bg: '#6b7280',
    content: (
      <>
        {/* Ears */}
        <circle cx="25" cy="40" r="16" fill="#6b7280" />
        <circle cx="75" cy="40" r="16" fill="#6b7280" />
        <circle cx="25" cy="40" r="10" fill="#d1d5db" />
        <circle cx="75" cy="40" r="10" fill="#d1d5db" />
        {/* Face */}
        <circle cx="50" cy="55" r="28" fill="#6b7280" />
        {/* Eyes */}
        <circle cx="40" cy="50" r="5" fill="#1f2937" />
        <circle cx="60" cy="50" r="5" fill="#1f2937" />
        <circle cx="42" cy="48" r="2" fill="white" />
        <circle cx="62" cy="48" r="2" fill="white" />
        {/* Nose */}
        <ellipse cx="50" cy="62" rx="10" ry="8" fill="#1f2937" />
      </>
    ),
  },
  tiger: {
    bg: '#f97316',
    content: (
      <>
        {/* Ears */}
        <circle cx="28" cy="30" r="10" fill="#f97316" />
        <circle cx="72" cy="30" r="10" fill="#f97316" />
        <circle cx="28" cy="30" r="5" fill="white" />
        <circle cx="72" cy="30" r="5" fill="white" />
        {/* Face */}
        <circle cx="50" cy="55" r="30" fill="#f97316" />
        {/* Stripes */}
        <path d="M35 35 Q40 45 35 55" stroke="#1f2937" strokeWidth="3" fill="none" />
        <path d="M65 35 Q60 45 65 55" stroke="#1f2937" strokeWidth="3" fill="none" />
        <path d="M50 30 L50 40" stroke="#1f2937" strokeWidth="3" />
        {/* White markings */}
        <ellipse cx="50" cy="68" rx="16" ry="12" fill="white" />
        {/* Eyes */}
        <circle cx="40" cy="52" r="5" fill="#1f2937" />
        <circle cx="60" cy="52" r="5" fill="#1f2937" />
        <circle cx="42" cy="50" r="2" fill="white" />
        <circle cx="62" cy="50" r="2" fill="white" />
        {/* Nose */}
        <polygon points="50,62 46,68 54,68" fill="#1f2937" />
      </>
    ),
  },
  wolf: {
    bg: '#4b5563',
    content: (
      <>
        {/* Ears */}
        <polygon points="25,40 32,12 42,40" fill="#4b5563" />
        <polygon points="58,40 68,12 75,40" fill="#4b5563" />
        <polygon points="28,38 32,18 38,38" fill="#9ca3af" />
        <polygon points="62,38 68,18 72,38" fill="#9ca3af" />
        {/* Face */}
        <ellipse cx="50" cy="55" rx="28" ry="30" fill="#4b5563" />
        {/* Snout */}
        <ellipse cx="50" cy="68" rx="14" ry="12" fill="#9ca3af" />
        {/* Eyes */}
        <ellipse cx="40" cy="50" rx="5" ry="6" fill="#fbbf24" />
        <ellipse cx="60" cy="50" rx="5" ry="6" fill="#fbbf24" />
        <ellipse cx="40" cy="50" rx="2" ry="4" fill="#1f2937" />
        <ellipse cx="60" cy="50" rx="2" ry="4" fill="#1f2937" />
        {/* Nose */}
        <ellipse cx="50" cy="65" rx="5" ry="4" fill="#1f2937" />
      </>
    ),
  },
  deer: {
    bg: '#d97706',
    content: (
      <>
        {/* Antlers */}
        <path
          d="M30 35 L25 15 M25 15 L20 10 M25 15 L30 10"
          stroke="#92400e"
          strokeWidth="4"
          fill="none"
          strokeLinecap="round"
        />
        <path
          d="M70 35 L75 15 M75 15 L80 10 M75 15 L70 10"
          stroke="#92400e"
          strokeWidth="4"
          fill="none"
          strokeLinecap="round"
        />
        {/* Ears */}
        <ellipse cx="25" cy="45" rx="6" ry="12" fill="#d97706" />
        <ellipse cx="75" cy="45" rx="6" ry="12" fill="#d97706" />
        {/* Face */}
        <ellipse cx="50" cy="58" rx="24" ry="28" fill="#d97706" />
        {/* Snout */}
        <ellipse cx="50" cy="72" rx="12" ry="10" fill="#fef3c7" />
        {/* Eyes */}
        <circle cx="40" cy="52" r="5" fill="#1f2937" />
        <circle cx="60" cy="52" r="5" fill="#1f2937" />
        <circle cx="42" cy="50" r="2" fill="white" />
        <circle cx="62" cy="50" r="2" fill="white" />
        {/* Nose */}
        <ellipse cx="50" cy="70" rx="4" ry="3" fill="#1f2937" />
      </>
    ),
  },
  robot: {
    bg: '#3b82f6',
    content: (
      <>
        {/* Antenna */}
        <line x1="50" y1="20" x2="50" y2="30" stroke="#1f2937" strokeWidth="3" />
        <circle cx="50" cy="16" r="5" fill="#ef4444" />
        {/* Head */}
        <rect x="25" y="30" width="50" height="45" rx="8" fill="#60a5fa" />
        {/* Screen face */}
        <rect x="30" y="38" width="40" height="30" rx="4" fill="#1e3a5f" />
        {/* Eyes */}
        <rect x="35" y="45" width="10" height="10" rx="2" fill="#4ade80" />
        <rect x="55" y="45" width="10" height="10" rx="2" fill="#4ade80" />
        {/* Mouth */}
        <rect x="40" y="58" width="20" height="4" rx="1" fill="#4ade80" />
        {/* Bolts */}
        <circle cx="22" cy="52" r="4" fill="#fbbf24" />
        <circle cx="78" cy="52" r="4" fill="#fbbf24" />
      </>
    ),
  },
  alien: {
    bg: '#22c55e',
    content: (
      <>
        {/* Head */}
        <ellipse cx="50" cy="55" rx="32" ry="35" fill="#4ade80" />
        {/* Big eyes */}
        <ellipse cx="38" cy="50" rx="12" ry="14" fill="#1f2937" />
        <ellipse cx="62" cy="50" rx="12" ry="14" fill="#1f2937" />
        <ellipse cx="38" cy="50" rx="8" ry="10" fill="#a855f7" />
        <ellipse cx="62" cy="50" rx="8" ry="10" fill="#a855f7" />
        <circle cx="40" cy="47" r="3" fill="white" />
        <circle cx="64" cy="47" r="3" fill="white" />
        {/* Mouth */}
        <ellipse cx="50" cy="72" rx="6" ry="3" fill="#15803d" />
        {/* Antennae */}
        <line x1="35" y1="22" x2="40" y2="35" stroke="#4ade80" strokeWidth="3" />
        <line x1="65" y1="22" x2="60" y2="35" stroke="#4ade80" strokeWidth="3" />
        <circle cx="35" cy="18" r="4" fill="#fbbf24" />
        <circle cx="65" cy="18" r="4" fill="#fbbf24" />
      </>
    ),
  },
  ghost: {
    bg: '#c4b5fd',
    content: (
      <>
        {/* Body */}
        <path
          d="M25 50 Q25 25 50 25 Q75 25 75 50 L75 80 Q68 72 60 80 Q52 72 50 80 Q48 72 40 80 Q32 72 25 80 Z"
          fill="white"
        />
        {/* Eyes */}
        <ellipse cx="40" cy="50" rx="7" ry="9" fill="#1f2937" />
        <ellipse cx="60" cy="50" rx="7" ry="9" fill="#1f2937" />
        <circle cx="42" cy="48" r="2" fill="white" />
        <circle cx="62" cy="48" r="2" fill="white" />
        {/* Blush */}
        <circle cx="32" cy="58" r="5" fill="#fecdd3" />
        <circle cx="68" cy="58" r="5" fill="#fecdd3" />
        {/* Mouth */}
        <ellipse cx="50" cy="65" rx="4" ry="3" fill="#1f2937" />
      </>
    ),
  },
  wizard: {
    bg: '#6366f1',
    content: (
      <>
        {/* Hat */}
        <polygon points="50,5 25,45 75,45" fill="#4f46e5" />
        <ellipse cx="50" cy="45" rx="28" ry="8" fill="#4f46e5" />
        {/* Stars on hat */}
        <text x="45" y="30" fill="#fbbf24" fontSize="10">
          ★
        </text>
        <text x="55" y="38" fill="#fbbf24" fontSize="6">
          ★
        </text>
        {/* Face */}
        <circle cx="50" cy="62" r="22" fill="#fcd9b6" />
        {/* Eyes */}
        <circle cx="43" cy="58" r="4" fill="#1f2937" />
        <circle cx="57" cy="58" r="4" fill="#1f2937" />
        <circle cx="44" cy="57" r="1.5" fill="white" />
        <circle cx="58" cy="57" r="1.5" fill="white" />
        {/* Eyebrows */}
        <path d="M38 52 Q43 50 48 52" stroke="#1f2937" strokeWidth="2" fill="none" />
        <path d="M52 52 Q57 50 62 52" stroke="#1f2937" strokeWidth="2" fill="none" />
        {/* Beard */}
        <path d="M35 70 Q50 90 65 70" fill="#e5e7eb" />
        <path d="M40 72 Q50 85 60 72" fill="#d1d5db" />
      </>
    ),
  },
  pig: {
    bg: '#f9a8d4',
    content: (
      <>
        {/* Ears */}
        <ellipse cx="28" cy="35" rx="12" ry="10" fill="#f472b6" transform="rotate(-20 28 35)" />
        <ellipse cx="72" cy="35" rx="12" ry="10" fill="#f472b6" transform="rotate(20 72 35)" />
        <ellipse cx="28" cy="35" rx="8" ry="6" fill="#f9a8d4" transform="rotate(-20 28 35)" />
        <ellipse cx="72" cy="35" rx="8" ry="6" fill="#f9a8d4" transform="rotate(20 72 35)" />
        {/* Face */}
        <circle cx="50" cy="55" r="30" fill="#f9a8d4" />
        {/* Eyes */}
        <circle cx="38" cy="48" r="5" fill="#1f2937" />
        <circle cx="62" cy="48" r="5" fill="#1f2937" />
        <circle cx="40" cy="46" r="2" fill="white" />
        <circle cx="64" cy="46" r="2" fill="white" />
        {/* Snout */}
        <ellipse cx="50" cy="65" rx="14" ry="10" fill="#f472b6" />
        {/* Nostrils */}
        <ellipse cx="44" cy="65" rx="4" ry="5" fill="#1f2937" />
        <ellipse cx="56" cy="65" rx="4" ry="5" fill="#1f2937" />
      </>
    ),
  },
  dragon: {
    bg: '#dc2626',
    content: (
      <>
        {/* Horns */}
        <polygon points="25,35 20,12 35,35" fill="#7f1d1d" />
        <polygon points="75,35 80,12 65,35" fill="#7f1d1d" />
        {/* Spikes */}
        <polygon points="50,18 45,30 55,30" fill="#7f1d1d" />
        {/* Face */}
        <ellipse cx="50" cy="55" rx="30" ry="30" fill="#ef4444" />
        {/* Snout */}
        <ellipse cx="50" cy="68" rx="18" ry="14" fill="#fca5a5" />
        {/* Eyes */}
        <ellipse cx="38" cy="48" rx="6" ry="8" fill="#fbbf24" />
        <ellipse cx="62" cy="48" rx="6" ry="8" fill="#fbbf24" />
        <ellipse cx="38" cy="48" rx="2" ry="5" fill="#1f2937" />
        <ellipse cx="62" cy="48" rx="2" ry="5" fill="#1f2937" />
        {/* Nostrils with smoke */}
        <circle cx="44" cy="68" r="3" fill="#1f2937" />
        <circle cx="56" cy="68" r="3" fill="#1f2937" />
        {/* Smoke puffs */}
        <circle cx="42" cy="62" r="2" fill="#9ca3af" opacity="0.6" />
        <circle cx="58" cy="62" r="2" fill="#9ca3af" opacity="0.6" />
      </>
    ),
  },
  ninja: {
    bg: '#1f2937',
    content: (
      <>
        {/* Head wrap */}
        <circle cx="50" cy="50" r="32" fill="#374151" />
        {/* Face opening */}
        <rect x="25" y="42" width="50" height="16" rx="4" fill="#fcd9b6" />
        {/* Eyes */}
        <ellipse cx="40" cy="50" rx="6" ry="4" fill="#1f2937" />
        <ellipse cx="60" cy="50" rx="6" ry="4" fill="#1f2937" />
        <circle cx="42" cy="49" r="1.5" fill="white" />
        <circle cx="62" cy="49" r="1.5" fill="white" />
        {/* Headband */}
        <rect x="20" y="35" width="60" height="6" fill="#ef4444" />
        {/* Headband knot */}
        <circle cx="78" cy="38" r="6" fill="#ef4444" />
        <path d="M82 32 L90 28" stroke="#ef4444" strokeWidth="4" />
        <path d="M82 44 L88 50" stroke="#ef4444" strokeWidth="4" />
      </>
    ),
  },
};

export const AVATAR_TYPES = Object.keys(animals) as (keyof typeof animals)[];

export function getRandomAvatar(): string {
  return AVATAR_TYPES[Math.floor(Math.random() * AVATAR_TYPES.length)] ?? 'cat';
}

interface ProfilePicturePropsExtended extends ProfilePictureProps {
  outlineColor?: string;
  outlineWidth?: number;
}

export function ProfilePicture({
  animal,
  size = 40,
  className,
  outlineColor,
  outlineWidth,
}: ProfilePicturePropsExtended) {
  const key = animal ?? 'cat';
  // Fallback to cat if key not found (cat is guaranteed to exist)
  const animalData = animals[key] ?? animals['cat'] ?? { bg: '#f472b6', content: <></> };

  // Calculate outline width based on size if not specified
  const effectiveOutlineWidth = outlineWidth ?? (size < 40 ? 1 : size < 80 ? 2 : 3);

  // Use the animal's background color as the default outline color
  const effectiveOutlineColor = outlineColor ?? animalData.bg;

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 100 100"
      width={size}
      height={size}
      className={className}
      style={{
        filter: `drop-shadow(0 0 ${effectiveOutlineWidth}px ${effectiveOutlineColor})`,
      }}
    >
      {animalData.content}
    </svg>
  );
}
